import React from 'react';

// The app's single icon set: stroke-based SVGs drawn on a 24x24 grid, colored via
// currentColor so they inherit button/nav text color. Use these instead of unicode
// glyphs or emoji, and reuse the same icon wherever an action means the same thing:
//   Edit → EditIcon · Delete/Remove → DeleteIcon · Watch/Watchlist → WatchIcon
//   Alert(s) → BellIcon · Chart(s) → ChartIcon · Pause/Resume → PauseIcon/PlayIcon
//   Refresh → RefreshIcon · Mark read/confirm → CheckIcon · Needs attention → WarningIcon
// Row actions use <button className="icon-btn"> (or "icon-btn-sm" in dense tables)
// with matching title + aria-label for hover text and screen readers.

const base = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

export const EditIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

export const DeleteIcon = (props) => (
  <svg {...base} {...props}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

export const WatchIcon = (props) => (
  <svg {...base} {...props}>
    <circle cx="6" cy="13" r="4" />
    <circle cx="18" cy="13" r="4" />
    <path d="M10 12h4" />
    <path d="M2 12l2 -1" />
    <path d="M22 12l-2 -1" />
  </svg>
);

export const BellIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

export const PauseIcon = (props) => (
  <svg {...base} {...props}>
    <rect x="6" y="4" width="4" height="16" fill="currentColor" stroke="none" />
    <rect x="14" y="4" width="4" height="16" fill="currentColor" stroke="none" />
  </svg>
);

export const PlayIcon = (props) => (
  <svg {...base} {...props}>
    <polygon points="6 3 20 12 6 21 6 3" fill="currentColor" stroke="none" strokeLinejoin="round" />
  </svg>
);

export const RefreshIcon = (props) => (
  <svg {...base} {...props}>
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

export const ClockIcon = (props) => (
  <svg {...base} {...props}>
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 16 14" />
  </svg>
);

export const ChartIcon = (props) => (
  <svg {...base} {...props} viewBox="0 0 24 20">
    <line x1="3" y1="6" x2="3" y2="15" />
    <rect x="1" y="8" width="4" height="5" fill="none" />
    <line x1="8" y1="2" x2="8" y2="11" />
    <rect x="6" y="4" width="4" height="5" fill="none" />
    <line x1="13" y1="9" x2="13" y2="17" />
    <rect x="11" y="11" width="4" height="4" fill="currentColor" />
    <line x1="18" y1="4" x2="18" y2="13" />
    <rect x="16" y="6" width="4" height="4" fill="none" />
  </svg>
);

export const CheckIcon = (props) => (
  <svg {...base} {...props}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const WarningIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

export const DashboardIcon = (props) => (
  <svg {...base} {...props}>
    <rect x="3" y="3" width="7" height="9" rx="1" />
    <rect x="14" y="3" width="7" height="5" rx="1" />
    <rect x="14" y="12" width="7" height="9" rx="1" />
    <rect x="3" y="16" width="7" height="5" rx="1" />
  </svg>
);

export const PositionsIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
    <path d="M22 12A10 10 0 0 0 12 2v10z" />
  </svg>
);

export const TransactionsIcon = (props) => (
  <svg {...base} {...props}>
    <polyline points="7 3 3 7 7 11" />
    <line x1="3" y1="7" x2="21" y2="7" />
    <polyline points="17 13 21 17 17 21" />
    <line x1="21" y1="17" x2="3" y2="17" />
  </svg>
);

export const DataIcon = (props) => (
  <svg {...base} {...props}>
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
  </svg>
);

export const SettingsIcon = (props) => (
  <svg {...base} {...props}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

export const AccountsIcon = (props) => (
  <svg {...base} {...props}>
    <line x1="3" y1="21" x2="21" y2="21" />
    <line x1="5" y1="17" x2="5" y2="11" />
    <line x1="10" y1="17" x2="10" y2="11" />
    <line x1="14" y1="17" x2="14" y2="11" />
    <line x1="19" y1="17" x2="19" y2="11" />
    <polygon points="12 2 20 7 4 7 12 2" />
  </svg>
);

export const ProfileIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const SignOutIcon = (props) => (
  <svg {...base} {...props}>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);
